import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';

import ErrorModal from '../../shared/components/UIElements/ErrorModal';
import LoadingSpinner from '../../shared/components/UIElements/LoadingSpinner';
import EvaluationList from '../components/EvaluationList';
import { useHttpClient } from '../../shared/hooks/http-hook';
import { AuthContext } from '../../shared/context/auth-context';
import './QuestionEvaluation.css';
import MainNavigation from '../../components/MainNavigation';
import Button from '../../shared/components/FormElements/Button/Button';
import LineChart from '../components/LineChart';

const QuestionEvaluation = props => {

    const auth = useContext(AuthContext);
    const questionId = useParams().questionId;
    const [question, setQuestion] = useState();
    const [evaluations, setEvaluations] = useState();
    const {isLoading, error, sendRequest, clearError} = useHttpClient();
    const [scoreData, setScoreData] = useState();
    const [plan, setPlan] = useState('free');

    useEffect(()=>{
        const fetchEvaluation = async() => {
            try {
                const responseData = await sendRequest(process.env.REACT_APP_BACKEND_URL_PLAN+`/question/${questionId}`, 'GET', null, {Authorization: 'Bearer '+auth.token});
                setQuestion(responseData.question);
                setEvaluations(responseData.evaluations);
                setPlan(responseData.plan);

                let numEvalRecord = responseData.question.numEvalRecord;
                if (numEvalRecord.length > 10) {
                    numEvalRecord = responseData.question.numEvalRecord.slice(-10);
                }

                let scoreRecord = responseData.question.scoresRecord;
                if (scoreRecord.length > 10) {
                    scoreRecord = responseData.question.scoresRecord.slice(-10);
                }
                setScoreData({
                    labels: scoreRecord.map((data) => {
                        return new Date(data.date).toLocaleDateString()
                    }),
                    datasets: [
                        {
                            label: 'Average',
                            data: scoreRecord.map((data) => {
                                return data.avgScore
                            }),
                            borderColor: 'rgb(110, 111, 247, 1)',
                            backgroundColor: (context) => {
                                const bgColor = [
                                    'rgb(110, 111, 247, 0.8)',
                                    'rgb(110, 111, 247, 0.6)',
                                    'rgb(110, 111, 247, 0.4)',
                                    'rgb(110, 111, 247, 0.2)',
                                ]
            
                                if (!context.chart.chartArea) {
                                    return;
                                }
                                const { ctx, data, chartArea:{top, bottom} } = context.chart;
                                const gradientBg = ctx.createLinearGradient(0, top, 0, bottom);
                                gradientBg.addColorStop(0, bgColor[0]);
                                gradientBg.addColorStop(0.33, bgColor[1]);
                                gradientBg.addColorStop(0.66, bgColor[2]);
                                gradientBg.addColorStop(1, bgColor[3]);
            
                                return gradientBg;
                            },
                            fill: true
                        },
                        {
                            label: 'Clarity',
                            data: scoreRecord.map((data) => {
                                return data.clarity
                            }),
                            borderColor: 'rgb(64, 143, 246, 1)',
                            backgroundColor: (context) => {
                                const bgColor = [
                                    'rgb(64, 143, 246, 0.8)',
                                    'rgb(64, 143, 246, 0.6)',
                                    'rgb(64, 143, 246, 0.4)',
                                    'rgb(64, 143, 246, 0.2)',
                                ]
            
                                if (!context.chart.chartArea) {
                                    return;
                                }
                                const { ctx, data, chartArea:{top, bottom} } = context.chart;
                                const gradientBg = ctx.createLinearGradient(0, top, 0, bottom);
                                gradientBg.addColorStop(0, bgColor[0]);
                                gradientBg.addColorStop(0.33, bgColor[1]);
                                gradientBg.addColorStop(0.66, bgColor[2]);
                                gradientBg.addColorStop(1, bgColor[3]);
            
                                return gradientBg;
                            },
                            fill: true
                        },
                        {
                            label: 'Confidence',
                            data: scoreRecord.map((data) => {
                                return data.confidence
                            }),
                            borderColor: 'rgb(105, 226, 154, 1)',
                            backgroundColor: (context) => {
                                const bgColor = [
                                    'rgb(105, 226, 154, 0.8)',
                                    'rgb(105, 226, 154, 0.6)',
                                    'rgb(105, 226, 154, 0.4)',
                                    'rgb(105, 226, 154, 0.2)',
                                ]
            
                                if (!context.chart.chartArea) {
                                    return;
                                }
                                const { ctx, data, chartArea:{top, bottom} } = context.chart;
                                const gradientBg = ctx.createLinearGradient(0, top, 0, bottom);
                                gradientBg.addColorStop(0, bgColor[0]);
                                gradientBg.addColorStop(0.33, bgColor[1]);
                                gradientBg.addColorStop(0.66, bgColor[2]);
                                gradientBg.addColorStop(1, bgColor[3]);
            
                                return gradientBg;
                            },
                            fill: true
                        },
                        {
                            label: 'Relevance',
                            data: scoreRecord.map((data) => {
                                return data.relevance
                            }),
                            borderColor: 'rgb(218, 233, 95, 1)',
                            backgroundColor: (context) => {
                                const bgColor = [
                                    'rgb(218, 233, 95, 0.8)',
                                    'rgb(218, 233, 95, 0.6)',
                                    'rgb(218, 233, 95, 0.4)',
                                    'rgb(218, 233, 95, 0.2)',
                                ]
            
                                if (!context.chart.chartArea) {
                                    return;
                                }
                                const { ctx, data, chartArea:{top, bottom} } = context.chart;
                                const gradientBg = ctx.createLinearGradient(0, top, 0, bottom);
                                gradientBg.addColorStop(0, bgColor[0]);
                                gradientBg.addColorStop(0.33, bgColor[1]);
                                gradientBg.addColorStop(0.66, bgColor[2]);
                                gradientBg.addColorStop(1, bgColor[3]);
            
                                return gradientBg;
                            },
                            fill: true
                        },
                        {
                            label: 'Job Fit',
                            data: scoreRecord.map((data) => {
                                return data.job_fit
                            }),
                            borderColor: 'rgb(210, 155, 233, 1)',
                            backgroundColor: (context) => {
                                const bgColor = [
                                    'rgb(210, 155, 233, 0.8)',
                                    'rgb(210, 155, 233, 0.6)',
                                    'rgb(210, 155, 233, 0.4)',
                                    'rgb(210, 155, 233, 0.2)',
                                ]
            
                                if (!context.chart.chartArea) {
                                    return;
                                }
                                const { ctx, data, chartArea:{top, bottom} } = context.chart;
                                const gradientBg = ctx.createLinearGradient(0, top, 0, bottom);
                                gradientBg.addColorStop(0, bgColor[0]);
                                gradientBg.addColorStop(0.33, bgColor[1]);
                                gradientBg.addColorStop(0.66, bgColor[2]);
                                gradientBg.addColorStop(1, bgColor[3]);
            
                                return gradientBg;
                            },
                            fill: true
                        }
                    ]
                });
            } catch (err) {}
        };
        fetchEvaluation();
    },[sendRequest, auth, questionId]);

    /*
    if (!isLoading && !question) {
        return <h3>Something went wrong!</h3>
    }
    */

    return <React.Fragment>
        {!isLoading && <MainNavigation />}
        <div className='questioneval-page'>
        <ErrorModal error={error} onClear={clearError} />
        {isLoading && <LoadingSpinner asOverlay />}
        {!isLoading && question && <motion.div className='question-eval-titles' 
            initial={{ opacity: 0, scale: 1 }} 
            animate={{ opacity: 1, scale: 1 }} 
            transition={{ delay: 0.25, duration: 0.5 }}
        >
            <h2>{question.question}</h2>
            <div className='answer-wrapper'>
                <p>Select evaluation to see the details.</p>
                <div className='answer-button'>
                    <Button to={`/question/answer/${question._id}/${question.question.replace(/\//g, '-')}`}><p style={{ marginTop: 0, marginBottom: 0, color: 'white', letterSpacing: '0.2rem' }}>ANSWER</p></Button>
                </div>
            </div>
            
        </motion.div>}
        {!isLoading && question && evaluations && (plan === 'free') &&
            <motion.div 
                className='questioneval-details' 
                initial={{ opacity: 0, scale: 1 }} 
                animate={{ opacity: 1, scale: 1 }} 
                transition={{ delay: 0.25, duration: 0.5 }}
            >
                <div className='evaluation-list-wrapper'>
                    <h3>Evaluations:</h3>
                    <EvaluationList items={evaluations} questionId={question._id} question={question.question}/>
                </div>
                <div className='evaluation-chart'>
                    <h3>Progress:</h3>
                    <div className='chart-div-free'>
                        <LineChart chartData={scoreData} options={{responsive: true}}/>
                    </div>
                </div>
            </motion.div>
            }
            {!isLoading && question && evaluations && (plan === 'pro') &&
            <motion.div 
                className='questioneval-details' 
                initial={{ opacity: 0, scale: 1 }} 
                animate={{ opacity: 1, scale: 1 }} 
                transition={{ delay: 0.25, duration: 0.5 }}
            >
                <div className='evaluation-list-wrapper'>
                    <h3>Evaluations:</h3>
                    <EvaluationList items={evaluations} questionId={question._id} question={question.question}/>
                </div>
                <div className='evaluation-chart'>
                    <h3>Progress:</h3>
                    <div className='chart-div'>
                        <LineChart chartData={scoreData} options={{responsive: true}}/>
                    </div>
                </div>
            </motion.div>
            }
        </div>
    </React.Fragment>;
};

export default QuestionEvaluation;